import { Dayjs } from "dayjs";
import { Seller, TransactionType } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { isAmountIncludesPartnerFee } from "../models/PartnerFeeFormula";
import { purchaseTypes } from "../models/Transaction";
import { AnalyticsMode } from "../stores/reports/analyticsFilterStore";

const index = config.elasticsearch.indexes.transaction;

export interface IncomeReport {
    charges: number;
    refunds: number;
    collections: number;
    processingFees: number;
    guestFees: number;
    partnerFees: number;
    taxes: number;
    income: number;
    transactionCount: number;
}

export interface RevenueReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    mode: AnalyticsMode;
    products: ID[];
}

export const getIncomeReport = async (params: RevenueReportParams): Promise<IncomeReport> => {
    const { seller } = params;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));
    return getReport(data, params.seller)!;
};

const getPayload = ({ seller, mode, products, from, to }: RevenueReportParams) => {
    const isCashFlow = mode === AnalyticsMode.CASH_FLOW;
    const rangeKey = isCashFlow ? "createdAt" : "items.realizedAt";
    const rangeFilter = { range: { [rangeKey]: { from: from.valueOf(), to: to.valueOf() } } };

    const purchaseFilters: TransactionType[] = [
        TransactionType.PURCHASE,
        TransactionType.DEPOSIT,
        TransactionType.BALANCE,
        TransactionType.PURCHASE_AFFILIATE,
    ];

    const incomeTypes = [
        TransactionType.PURCHASE,
        TransactionType.DEPOSIT,
        TransactionType.BALANCE,
        TransactionType.PURCHASE_AFFILIATE,
        TransactionType.REFUND_COMMISSION,
    ];

    const purchaseFilter = { terms: { type: purchaseFilters } };
    const allFilter = { terms: { type: purchaseTypes } };
    const itemsFilter: any = [];

    const chargesFilter = { bool: { must: [purchaseFilter] } };

    const query: any = {
        size: 0,
        aggs: {
            revenueBreakdown: {
                aggs: {
                    charges: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: { amount_stats: { stats: { field: "items.gross" } } },
                                    },
                                },
                            },
                        },
                        filter: chargesFilter,
                    },
                    processingFees: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: {
                                            amount_stats: {
                                                stats: { field: "items.commissionSplitAmount.creditCard" },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                        filter: { terms: { type: incomeTypes } },
                    },
                    bookingFees: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: {
                                            amount_stats: { stats: { field: "items.commissionSplitAmount.offline" } },
                                        },
                                    },
                                },
                            },
                        },
                        filter: { terms: { type: incomeTypes } },
                    },
                    partnerFees: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: {
                                            amount_stats: { stats: { field: "items.commissionSplitAmount.seller" } },
                                        },
                                    },
                                },
                            },
                        },
                        filter: {
                            terms: {
                                type: incomeTypes,
                            },
                        },
                    },
                    travelerPartnerFees: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: {
                                            amount_stats: { stats: { field: "items.commissionSplitAmount.traveler" } },
                                        },
                                    },
                                },
                            },
                        },
                        filter: {
                            terms: {
                                type: incomeTypes,
                            },
                        },
                    },
                    taxes: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: { amount_stats: { stats: { field: "items.taxFee" } } },
                                    },
                                },
                            },
                        },
                        filter: allFilter,
                    },
                    refund: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: { amount_stats: { stats: { field: "items.gross" } } },
                                    },
                                },
                            },
                        },
                        filter: { terms: { type: [TransactionType.REFUND] } },
                    },
                    redeem: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: { amount_stats: { stats: { field: "items.gross" } } },
                                    },
                                },
                            },
                        },
                        filter: { terms: { type: [TransactionType.REDEEM] } },
                    },
                    pluginFees: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: { amount_stats: { stats: { field: "items.net" } } },
                                    },
                                },
                            },
                        },
                        filter: { terms: { type: [TransactionType.PLUGIN_FEE] } },
                    },
                    ordersIncome: {
                        aggs: {
                            items: {
                                nested: { path: "items" },
                                aggs: {
                                    filtered: {
                                        filter: { bool: { must: itemsFilter } },
                                        aggs: {
                                            amount_stats: {
                                                stats: { field: isCashFlow ? "items.net" : "items.realizableAmount" },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                        filter: { bool: { must: [allFilter] } },
                    },
                },
                filters: { filters: { all: { match_all: { boost: 1 } } } },
            },
        },
        query: {
            bool: {
                must: [
                    { nested: { path: "items", query: { bool: { must: [] } } } },
                    allFilter,
                    { term: { "seller.id": seller.id } },
                ],
            },
        },
    };

    if (isCashFlow) {
        query.query.bool.must.push(rangeFilter);
    } else {
        query.query.bool.must[0].nested.query.bool.must.push(rangeFilter);
        itemsFilter.push(rangeFilter);
    }

    if (products.length > 0) {
        // Replace the default all filter with one that explicitly specifies the products
        const productFilter = { terms: { "items.orderItem.product.id": products } };
        query.query.bool.must[0].nested.query.bool.must.push(productFilter);
        itemsFilter.push(productFilter);
    }

    return query;
};

const defaultValues = {
    charges: 0,
    refunds: 0,
    collections: 0,
    processingFees: 0,
    guestFees: 0,
    partnerFees: 0,
    taxes: 0,
    income: 0,
    transactionCount: 0,
};

const getReport = (data: any, seller: Seller): IncomeReport => {
    if (!data.aggregations) {
        console.error("No facets found for revenue breakdown");
        return defaultValues;
    }

    const breakdown = data.aggregations.revenueBreakdown.buckets.all;
    const charges: { sum: number } = breakdown.charges.items.filtered.amount_stats;
    const refunds: { sum: number } = breakdown.refund.items.filtered.amount_stats;
    const redemptions: { sum: number } = breakdown.redeem.items.filtered.amount_stats;
    const pluginFees = breakdown.pluginFees.items.filtered.amount_stats;
    const collections = charges.sum + refunds.sum + redemptions.sum;
    const taxes = breakdown.taxes.items.filtered.amount_stats;
    const processingFees = breakdown.processingFees.items.filtered.amount_stats;
    const guestFees = breakdown.bookingFees.items.filtered.amount_stats;
    const transactionCount = breakdown.doc_count;
    const income = breakdown.ordersIncome.items.filtered.amount_stats.sum - taxes.sum;

    let partnerFees = breakdown.partnerFees.items.filtered.amount_stats;
    if (isAmountIncludesPartnerFee(seller.partnerFeeFormulas)) {
        partnerFees = breakdown.travelerPartnerFees.items.filtered.amount_stats;
    }

    return {
        charges: charges.sum + redemptions.sum,
        refunds: refunds.sum,
        processingFees: processingFees.sum - pluginFees.sum,
        guestFees: guestFees.sum,
        partnerFees: partnerFees.sum,
        taxes: taxes.sum,
        income,
        collections,
        transactionCount,
    };
};
